/**
 * S-03 — support-only request-log analytics: overview, per-path breakdown, CSV export.
 *
 * The breakdown is a table (components/ui/VirtualTable) with search (path), area and errors
 * filters, sort (+ direction) and a batch size for the pages loaded as you scroll. The API returns
 * every path the period saw, so these run in the browser (lib/clientList.ts).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { analyticsBreakdown, analyticsOverview } from "../../api/analytics";
import type { AnalyticsBreakdown } from "../../api/analytics";
import { describeError } from "../../api/errors";
import { ExportButton } from "../../components/ui/ExportButton";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  SearchBox,
  SelectFilter,
  SortFilter,
} from "../../components/ui/FilterBar";
import { Select } from "../../components/ui/Select";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { narrowAndSort, useClientPages } from "../../lib/clientList";
import type { SortValue } from "../../lib/clientList";
import type { TableColumn } from "../../lib/tableColumns";
import type { ExportColumn } from "../../lib/tableExport";

type Row = AnalyticsBreakdown["rows"][number];

const ms = (v: number | null) => (v != null ? `${v} ms` : "—");

/** The part of the API a path belongs to: the segment after /api/v1 ("admin", "patient", "me"…). */
const areaOf = (path: string) => /^\/api\/v\d+\/([^/?]+)/.exec(path)?.[1] ?? "other";

type Errors = "" | "with" | "without";
const ERROR_OPTIONS: { value: Errors; label: string }[] = [
  { value: "", label: "All" },
  { value: "with", label: "With errors" },
  { value: "without", label: "Without errors" },
];

type SortKey = "requests" | "errors" | "p95" | "path";
const SORTS: { value: SortKey; label: string }[] = [
  { value: "requests", label: "Requests" },
  { value: "errors", label: "Errors" },
  { value: "p95", label: "p95 latency" },
  { value: "path", label: "Path" },
];
const SORT_VALUE: Record<SortKey, (r: Row) => SortValue> = {
  requests: (r) => r.request_count,
  errors: (r) => r.error_count,
  p95: (r) => r.p95_latency_ms,
  path: (r) => r.path_pattern,
};
// The direction each sort reads most naturally in, applied when the sort changes: the most first.
const NATURAL_DESC: Record<SortKey, boolean> = { requests: true, errors: true, p95: true, path: false };

// The numeric columns keep their places; a long path wraps instead of widening the table. Errors
// from sm (a phone shows them under the path).
const COLUMNS: TableColumn[] = [
  { key: "path", header: "Path", width: "minmax(0,1fr)" },
  { key: "requests", header: "Requests", width: { base: "4.75rem", sm: "6rem" }, align: "end" },
  { key: "errors", header: "Errors", width: "5rem", from: "sm", align: "end" },
  { key: "p95", header: "p95 latency", width: { base: "5.75rem", sm: "7rem" }, align: "end" },
];

const EXPORT_COLUMNS: ExportColumn<Row>[] = [
  { header: "Path", value: (r) => r.path_pattern },
  { header: "Area", value: (r) => areaOf(r.path_pattern) },
  { header: "Requests", value: (r) => r.request_count },
  { header: "Errors", value: (r) => r.error_count },
  { header: "Error rate %", value: (r) => (r.request_count ? Math.round((r.error_count / r.request_count) * 10000) / 100 : 0) },
  { header: "p95 latency (ms)", value: (r) => r.p95_latency_ms },
];

export function RequestAnalytics() {
  const [days, setDays] = useState(7);
  const { data: overview } = useQuery({ queryKey: ["an-overview", days], queryFn: () => analyticsOverview(days) });
  const breakdown = useQuery({ queryKey: ["an-breakdown", days], queryFn: () => analyticsBreakdown(days) });

  // table controls
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const [errors, setErrors] = useState<Errors>("");
  const [sort, setSort] = useState<SortKey>("requests");
  const [descending, setDescending] = useState(true);
  const [perPage, setPerPage] = useState(25);

  const rows: Row[] = breakdown.data?.rows ?? [];
  // The areas the period's paths fall in, plus the one picked (it may have no paths in a new period).
  const areaOptions = [
    { value: "", label: "All" },
    ...[...new Set([...rows.map((r) => areaOf(r.path_pattern)), area].filter(Boolean))].sort().map((a) => ({ value: a, label: a })),
  ];
  const matching = narrowAndSort(rows, {
    q,
    searchText: (r) => r.path_pattern,
    keep: (r) =>
      (!area || areaOf(r.path_pattern) === area) &&
      (errors === "" || (errors === "with" ? r.error_count > 0 : r.error_count === 0)),
    sortValue: SORT_VALUE[sort],
    descending,
  });
  const { page, total, hasMore, loadMore } = useClientPages(
    matching,
    perPage,
    JSON.stringify([days, q, area, errors, sort, descending, perPage]),
  );
  const narrowing = (area ? 1 : 0) + (errors ? 1 : 0);
  const activeFilters = narrowing + (q.trim() ? 1 : 0);

  return (
    <div className="flex h-full max-w-3xl flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Request analytics</h2>
        <div className="flex gap-2">
          <Select
            value={days}
            options={[1, 7, 30].map((d) => ({ value: d, label: `Last ${d}d` }))}
            onChange={setDays}
            aria-label="Period"
            className="text-sm"
          />
          {/* The period's paths that the search and filters match, all of them, in the table's order
              (the server's breakdown.csv ignores the table's filters). */}
          <ExportButton table="request paths" columns={EXPORT_COLUMNS} rows={matching} disabled={!breakdown.data} />
        </div>
      </div>

      {overview && (
        <div className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Requests" value={String(overview.total_requests)} />
          <Stat label="Errors" value={String(overview.total_errors)} />
          <Stat label="Error rate" value={`${Math.round(overview.error_rate * 100)}%`} />
          <Stat label="Latency p50 · p95" value={overview.p50_latency_ms != null ? `${overview.p50_latency_ms} · ${ms(overview.p95_latency_ms)}` : "—"} />
        </div>
      )}

      <FilterBar
        label="Search and filter endpoints"
        narrowing={narrowing}
        search={<SearchBox value={q} onChange={setQ} placeholder="Search by path…" />}
      >
        <SelectFilter label="Area" value={area} options={areaOptions} onChange={setArea} testId="analytics-filter-area" />
        <SelectFilter label="Errors" value={errors} options={ERROR_OPTIONS} onChange={setErrors} testId="analytics-filter-errors" />
        <SortFilter
          value={sort}
          options={SORTS}
          descending={descending}
          onChange={(s) => {
            setSort(s);
            setDescending(NATURAL_DESC[s]);
          }}
          onToggle={() => setDescending((d) => !d)}
        />
        <BatchSizeFilter value={perPage} onChange={setPerPage} />
        <ClearFilters
          count={activeFilters}
          onClear={() => {
            setQ("");
            setArea("");
            setErrors("");
          }}
        />
      </FilterBar>

      <VirtualTable
        label="Requests by endpoint"
        toolbar={<div className="shrink-0 border-b px-4 py-3 text-sm font-medium">By endpoint</div>}
        columns={COLUMNS}
        rows={page}
        rowKey={(r) => r.path_pattern}
        rowHeight={44}
        measureRows
        loading={breakdown.isLoading}
        error={breakdown.isError ? describeError(breakdown.error) : null}
        onRetry={() => void breakdown.refetch()}
        empty={activeFilters ? "No endpoints match your filters." : "No requests in this period."}
        total={total}
        hasMore={hasMore}
        onLoadMore={loadMore}
        resetKey={JSON.stringify([days, q, area, errors, sort, descending])}
        renderRow={(r) => (
          <TableRow
            columns={COLUMNS}
            className="py-2 text-sm"
            cells={{
              path: (
                <div className="font-mono text-xs break-all">
                  {r.path_pattern}
                  <div className="font-sans text-gray-400 sm:hidden">
                    {r.error_count} error{r.error_count === 1 ? "" : "s"}
                  </div>
                </div>
              ),
              requests: <span className="tabular-nums">{r.request_count}</span>,
              errors: <span className="tabular-nums">{r.error_count}</span>,
              p95: <span className="tabular-nums">{ms(r.p95_latency_ms)}</span>,
            }}
          />
        )}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-xs">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
